/* Product page behaviour that sits outside any one block.
   Loaded by blocks/shaped-product-header.liquid (and, on older templates, by
   blocks/bnpl-price-teaser.liquid). The guard below makes a second copy a
   no-op, so both can include it.

   0. Media bar. A slim row directly UNDER the product image that holds the
      gallery dots and the Size chart button. Both used to float over the
      bottom of the photo, where they sat on top of review-card and lifestyle
      images (worst on phones). window.ShapedMediaBar() finds or creates the
      bar; the Fit Finder Badge block uses the same class name, so whichever
      script runs first creates it and the other reuses it.

   1. Gallery dots. The theme's <product-carousel> is set to
      slide-pagination-type="bullets" but renders none - the thumbnail strip
      takes its place. The strip is hidden in CSS and these dots replace it.
      Navigation goes through the Swiper instance on [main-slider] (.swiper),
      active state from its slideChange event. Clicking the hidden 1px
      thumbnails instead did not work, so don't go back to that.

   2. Review scroll. Anything with [data-scroll-reviews], plus the Reviews
      block's summary line, scrolls to the full Reviews block. Delegated from
      document so links inside sheets built later work too.

   3. Size guide link. The bundle block's "Size guide" link has no URL, so it
      opens the size guide drawer (Fit Finder Badge / Colour Swatches). A real
      URL in the bundle block's setting still wins.

   4. Pay later. window.ShapedPayLater:
        fillInline(button, cents)   one-line "[logo] 3 × R… | [logo] 4 × R… (i)"
        open(cents, context)        the detail sheet
        money(cents)                the page's price format, R699.95 / R1,299.00
      Used by the product header, the photo sheet, and a row injected under the
      bundle's Add to bag button that follows the bundle's payable total.
      Lender names, logos, counts and schedules come from the JSON config the
      product header block prints (#shaped-pay-config).

      Instalment amounts follow each lender's own widget: PayJustNow rounds
      each payment up (R699.95 -> 3 x R233.32), Payflex rounds down
      (-> 4 x R174.98). The lender settles the last payment's cents at checkout.

      The (i) is an SVG, not a letter in a circle: a glyph's position inside a
      box depends on the font's metrics, so it never sat centred.

   5. Keep the size when the colour changes (28 Sep 2026). Picking a size and
      then switching colourway used to drop the size back to "Choose size",
      even when the new colour had that size in stock. Shoppers read that as
      the page undoing their choice - it came back as "I'm trying to select
      large but it keeps going back to xs" on an abandoned checkout, the same
      weekend as a run of wrong-size orders.

      This does NOT touch the bundle block. It listens for a swatch click in
      the CAPTURE phase, reads the size the shopper already chose before the
      block tears the slot down, then re-applies it once the new colour's slot
      has rendered - by setting the select and firing a real change event, the
      same path a shopper takes, so the block updates its own state normally.

      It deliberately gives up rather than guessing: if the new colour has no
      such size, or that size is sold out, the slot is left on "Choose size"
      for the shopper to decide. Silently moving someone to a neighbouring
      size is how wrong-size orders happen in the first place.

   Prices: every price written by this file, the photo sheet, the bundle block
   and the product header uses one format - R699.95, R1,299.00.
*/
(function () {
  'use strict';
  if (window.__shapedPdp) return;
  window.__shapedPdp = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var INFO_SVG = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="7.25" fill="none" stroke="currentColor" stroke-width="1"/>' +
    '<circle cx="8" cy="4.9" r=".95" fill="currentColor"/>' +
    '<rect x="7.25" y="6.9" width="1.5" height="4.7" rx=".75" fill="currentColor"/></svg>';

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* ---------------- 0. media bar ---------------- */
  function mediaBar() {
    var existing = document.querySelector('.shaped-media-bar');
    if (existing) return existing;
    var carousel = document.querySelector('product-carousel');
    if (!carousel) return null;
    var stage = carousel.querySelector(':scope > .swiper') || carousel.querySelector('.swiper');
    var bar = el('div', 'shaped-media-bar');
    bar.appendChild(el('div', 'shaped-media-bar__start'));
    bar.appendChild(el('div', 'shaped-media-bar__mid'));
    bar.appendChild(el('div', 'shaped-media-bar__end'));
    if (stage && stage.parentNode) stage.insertAdjacentElement('afterend', bar);
    else carousel.appendChild(bar);
    return bar;
  }
  window.ShapedMediaBar = mediaBar;

  /* ---------------- 1. gallery dots ---------------- */
  function initDots() {
    var carousel = document.querySelector('product-carousel');
    if (!carousel || carousel.dataset.shapedDots === '1') return;
    var main = carousel.querySelector('[main-slider]');
    if (!main) return;
    var slides = main.querySelectorAll('.swiper-slide');
    if (slides.length < 2) return;
    var bar = mediaBar();
    if (!bar) return;
    carousel.dataset.shapedDots = '1';

    var wrap = el('div', 'shaped-dots');
    wrap.setAttribute('role', 'tablist');
    wrap.setAttribute('aria-label', 'Product images');
    for (var i = 0; i < slides.length; i++) {
      var dot = el('button', 'shaped-dot');
      dot.type = 'button';
      dot.dataset.index = i;
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + slides.length);
      wrap.appendChild(dot);
    }
    bar.querySelector('.shaped-media-bar__mid').appendChild(wrap);

    function instance() { return main.swiper || null; }
    function activeIndex() {
      var sw = instance();
      if (sw) return typeof sw.realIndex === 'number' ? sw.realIndex : sw.activeIndex;
      var cur = main.querySelectorAll('.swiper-slide');
      for (var i = 0; i < cur.length; i++) if (cur[i].classList.contains('swiper-slide-active')) return i;
      return 0;
    }
    function sync() {
      var active = activeIndex();
      var dots = wrap.querySelectorAll('.shaped-dot');
      for (var j = 0; j < dots.length; j++) {
        dots[j].classList.toggle('is-on', j === active);
        dots[j].setAttribute('aria-selected', j === active ? 'true' : 'false');
      }
    }
    wrap.addEventListener('click', function (e) {
      var d = e.target.closest('.shaped-dot');
      if (!d) return;
      var sw = instance();
      if (sw && typeof sw.slideTo === 'function') { sw.slideTo(+d.dataset.index); sync(); return; }
      var thumb = carousel.querySelectorAll('[thumbnail-slider] .swiper-slide')[+d.dataset.index];
      if (thumb) thumb.click();
    });
    var sw = instance();
    if (sw && typeof sw.on === 'function') { sw.on('slideChange', sync); sw.on('transitionEnd', sync); }
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; sync(); });
    }).observe(main, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
    sync();
    setTimeout(sync, 300);
    setTimeout(sync, 1200);
  }

  /* ---------------- 2. review scroll ---------------- */
  function scrollToReviews() {
    var full = document.querySelector('.sr:not(.sr--summary)');
    if (!full) return false;
    if (!full.id) full.id = 'shaped-reviews';
    var startY = window.scrollY;
    full.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
    // Some page-level scroll locks swallow smooth scrolling outright.
    setTimeout(function () {
      if (Math.abs(window.scrollY - startY) < 2) full.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 250);
    return true;
  }

  /* ---------------- 4. pay later ---------------- */
  var PAY = (function () {
    var cfg = null;
    try {
      var node = document.getElementById('shaped-pay-config');
      if (node) cfg = JSON.parse(node.textContent);
    } catch (e) { cfg = null; }
    var d = {
      symbol: 'R',
      plans: [
        { name: 'PayJustNow', logo: '', n: 3, round: 'up', cadence: 'Monthly, interest and fee free.', schedule: ['Today', '+1 month', '+2 months'] },
        { name: 'Payflex', logo: '', n: 4, round: 'down', cadence: 'Every two weeks, 0% interest.', schedule: ['Today', '+2 wks', '+4 wks', '+6 wks'] }
      ],
      heading_item: 'Pay later on [amount]',
      heading_basket: 'Pay later on your [amount] order',
      fine: 'Choose either at checkout. Approval is by the lender, and the first payment is taken at checkout.'
    };
    if (!cfg) return d;
    d.plans = (cfg.plans && cfg.plans.length) ? cfg.plans : d.plans;
    ['symbol', 'heading_item', 'heading_basket', 'fine'].forEach(function (k) { if (cfg[k]) d[k] = cfg[k]; });
    return d;
  })();

  function money(cents) {
    return PAY.symbol + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function perPayment(cents, plan) {
    return plan.round === 'down' ? Math.floor(cents / plan.n) : Math.ceil(cents / plan.n);
  }
  function brand(plan, cls) {
    if (plan.logo) {
      var img = el('img', cls);
      img.src = plan.logo;
      img.alt = plan.name;
      return img;
    }
    return el('span', cls + ' spl-word', plan.name);
  }

  function fillInline(btn, cents) {
    btn.textContent = '';
    btn.dataset.amount = cents;
    PAY.plans.forEach(function (plan, i) {
      if (i) btn.appendChild(el('span', 'spl-sep'));
      var pair = el('span', 'spl-pair');
      pair.appendChild(brand(plan, 'spl-logo'));
      pair.appendChild(el('b', null, plan.n + ' × ' + money(perPayment(cents, plan))));
      btn.appendChild(pair);
    });
    var info = el('span', 'spl-i');
    info.innerHTML = INFO_SVG;
    btn.appendChild(info);
    btn.setAttribute('aria-label', 'Pay later options: ' + PAY.plans.map(function (p) {
      return p.name + ' ' + p.n + ' payments of ' + money(perPayment(cents, p));
    }).join(', or ') + '. Show details.');
    btn.hidden = !(cents > 0);
  }

  var sheet, sheetBody, sheetTitle, lastFocus;
  function buildSheet() {
    sheet = el('div', 'spl-sheet');
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="spl-bg" data-spl-close></div>' +
      '<div class="spl-card" role="dialog" aria-modal="true" aria-labelledby="spl-title">' +
        '<span class="spl-grab" aria-hidden="true"></span>' +
        '<div class="spl-head"><h2 class="spl-title" id="spl-title"></h2>' +
        '<button type="button" class="spl-x" data-spl-close aria-label="Close">&#x2715;</button></div>' +
        '<div class="spl-plans"></div>' +
        '<p class="spl-fine"></p>' +
      '</div>';
    document.body.appendChild(sheet);
    sheetBody = sheet.querySelector('.spl-plans');
    sheetTitle = sheet.querySelector('.spl-title');
    sheet.querySelector('.spl-fine').textContent = PAY.fine;
    sheet.addEventListener('click', function (e) { if (e.target.closest('[data-spl-close]')) closeSheet(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet && !sheet.hidden) closeSheet();
    });
  }
  function openSheet(cents, context) {
    if (!sheet) buildSheet();
    lastFocus = document.activeElement;
    var tpl = context === 'basket' ? PAY.heading_basket : PAY.heading_item;
    sheetTitle.textContent = tpl.replace('[amount]', money(cents));
    sheetBody.textContent = '';
    PAY.plans.forEach(function (plan) {
      var card = el('div', 'spl-plan');
      var top = el('div', 'spl-plan-top');
      top.appendChild(brand(plan, 'spl-plan-logo'));
      top.appendChild(el('strong', null, plan.n + ' × ' + money(perPayment(cents, plan))));
      card.appendChild(top);
      card.appendChild(el('p', null, plan.cadence));
      var steps = el('div', 'spl-steps');
      steps.style.gridTemplateColumns = 'repeat(' + (plan.schedule || []).length + ', 1fr)';
      (plan.schedule || []).forEach(function (s) { steps.appendChild(el('span', null, s)); });
      card.appendChild(steps);
      sheetBody.appendChild(card);
    });
    sheet.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { var x = sheet.querySelector('.spl-x'); if (x) x.focus({ preventScroll: true }); });
  }
  function closeSheet() {
    if (!sheet || sheet.hidden) return;
    sheet.hidden = true;
    // leave the scroll lock if the photo sheet is still open underneath
    if (!document.querySelector('.sps:not([hidden])')) document.body.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  function payableCents(root) {
    var amt = root.querySelector('.sbt-amt');
    if (!amt) return 0;
    var digits = (amt.textContent || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : 0;
  }
  function initBundleRow() {
    document.querySelectorAll('.sbt').forEach(function (root) {
      if (root.dataset.shapedPay === '1') return;
      var add = root.querySelector('.sbt-add');
      var total = root.querySelector('.sbt-total');
      if (!add || !total) return;
      root.dataset.shapedPay = '1';
      var row = el('button', 'spl-inline spl-under');
      row.type = 'button';
      row.dataset.paylater = '';
      row.dataset.context = 'basket';
      add.insertAdjacentElement('afterend', row);
      var update = function () { fillInline(row, payableCents(root)); };
      update();
      new MutationObserver(update).observe(total, { childList: true, subtree: true, characterData: true });
    });
  }
  function initInlineButtons() {
    document.querySelectorAll('[data-paylater][data-fill]').forEach(function (btn) {
      if (btn.dataset.filled === '1') return;
      btn.dataset.filled = '1';
      fillInline(btn, parseInt(btn.dataset.amount, 10) || 0);
    });
  }

  window.ShapedPayLater = {
    fillInline: function (btn, cents) { fillInline(btn, cents); btn.dataset.context = 'item'; },
    open: openSheet,
    money: money
  };

  /* ---------------- 5. keep the size across a colour change ---------------- */

  /* "2XL — sold out" and "2XL - sold out" both reduce to "2XL". Sizes carry no
     hyphen of their own, so splitting on a spaced hyphen is safe. */
  function sizeLabel(text) {
    return String(text || '').split('—')[0].split(' - ')[0].replace(/\s+/g, ' ').trim().toUpperCase();
  }

  // The slot is rebuilt on a colour change, so it has to be found again by index.
  function slotByIndex(i) {
    var chip = document.querySelector('.sbt-chip[data-slot="' + i + '"]');
    return chip ? chip.closest('.sbt-slot') : null;
  }

  /* The rebuild is asynchronous and there is no event for it, so poll briefly.
     Every exit clears the timer: this must never be left running. */
  function restoreSize(index, wanted) {
    var tries = 0;
    var timer = setInterval(function () {
      if (++tries > 20) { clearInterval(timer); return; }

      var slot = slotByIndex(index);
      if (!slot) return;                       // still being rebuilt
      var sel = slot.querySelector('.sbt-size');
      if (!sel) return;                        // collapsed, or not rendered yet
      if (sel.selectedIndex > 0) { clearInterval(timer); return; }  // already chosen

      for (var k = 1; k < sel.options.length; k++) {
        var opt = sel.options[k];
        if (sizeLabel(opt.text) !== wanted) continue;
        clearInterval(timer);
        // Sold out in this colour: leave "Choose size" rather than move them.
        if (opt.disabled || /sold out/i.test(opt.text)) return;
        sel.value = opt.value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      clearInterval(timer);                    // this colour has no such size
    }, 60);
  }

  function initKeepSize() {
    if (document.documentElement.dataset.shapedKeepSize === '1') return;
    document.documentElement.dataset.shapedKeepSize = '1';
    // Capture phase: read the chosen size BEFORE the block rebuilds the slot.
    document.addEventListener('click', function (e) {
      var sw = e.target.closest && e.target.closest('.sbt-sw');
      if (!sw) return;
      var slot = sw.closest('.sbt-slot');
      if (!slot) return;
      var sel = slot.querySelector('.sbt-size');
      if (!sel || sel.selectedIndex < 1) return;   // nothing chosen yet
      if (sw.getAttribute('aria-pressed') === 'true') return;  // same colour
      restoreSize(sw.getAttribute('data-slot') || '0', sizeLabel(sel.options[sel.selectedIndex].text));
    }, true);
  }

  /* ---------------- delegated clicks ---------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var pay = t.closest('[data-paylater]');
    if (pay) {
      e.preventDefault();
      openSheet(parseInt(pay.dataset.amount, 10) || 0, pay.dataset.context || 'item');
      return;
    }

    var rev = t.closest('[data-scroll-reviews], .sr--summary a');
    if (rev) {
      if (scrollToReviews()) e.preventDefault();
      return;
    }

    var guide = t.closest('.sbt-guide');
    if (guide) {
      var href = (guide.getAttribute('href') || '').trim();
      if (href && href !== '#') return;
      var drawer = document.querySelector('[id^="size-guide-drawer-"]');
      if (!drawer) return;
      e.preventDefault();
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  });

  function boot() {
    initDots();
    initInlineButtons();
    initBundleRow();
    initKeepSize();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Swiper initialises late and the gallery is rebuilt on variant change.
  setTimeout(boot, 800);
  setTimeout(boot, 2000);
})();